/**
 * The people a person writes to (migration 0041): recorded on every send,
 * read once from the Sent folder, and offered in To, Cc and Bcc. Kept by the
 * platform, so webmail and the desktop app share one list.
 */

import type { RecentPeople, RecentPerson, SendRequest } from 'zitimail-shared-types';

import { api } from '../admin/api';
import { mail } from '../api/mail';

const base = (tenant: string) => `/tenants/${encodeURIComponent(tenant)}/my-recipients`;

export const recentPeople = {
  search: (tenant: string, q: string) =>
    api<RecentPeople>(`${base(tenant)}?q=${encodeURIComponent(q)}`),
  forget: (tenant: string, address: string) =>
    api<void>(`${base(tenant)}/forget`, { method: 'POST', body: { address } }),
};

/** `Name <a@b>`, `"Tan, E" <a@b>` or `a@b`, as an address and a name. */
export function person(entry: string): RecentPerson | null {
  const text = entry.trim();
  const angled = /^"?([^"<]*?)"?\s*<([^<>\s]+@[^<>\s]+)>$/.exec(text);
  if (angled) {
    return { address: angled[2] ?? '', name: (angled[1] ?? '').trim() };
  }
  return /^[^\s<>@]+@[^\s<>@]+$/.test(text) ? { address: text, name: '' } : null;
}

/** Records who a message went to. Never fails the send it follows. */
export function rememberRecipients(tenant: string | undefined, request: SendRequest): void {
  if (!tenant) {
    return;
  }
  const people = [...request.to, ...(request.cc ?? []), ...(request.bcc ?? [])]
    .map(person)
    .filter((p): p is RecentPerson => p !== null);
  if (people.length === 0) {
    return;
  }
  void api(`${base(tenant)}/learn`, { method: 'POST', body: { people } }).catch(() => undefined);
}

let seeding: Promise<void> | null = null;

/**
 * Reads the Sent folder into the list, once a mailbox (the platform keeps
 * only the first reading) and at most once a page. Where Sent cannot be read
 * (the desktop app has no such reader yet) the next webmail visit does it.
 */
export function seedFromSent(tenant: string): void {
  seeding ??= mail
    .sentRecipients()
    .then(({ people }) =>
      api(`${base(tenant)}/learn`, { method: 'POST', body: { people, seed: true } }),
    )
    .then(() => undefined)
    .catch(() => undefined);
}

/** For tests: forget that this page already read Sent. */
export function resetSeedingForTests(): void {
  seeding = null;
}
